/**
 * SitInFab — global floating microphone button.
 *
 * Mounted once at the App level (App.tsx) so it's visible on every
 * Coast surface post-login. One click opens the SitInListener modal.
 *
 * Design: small gold circle, bottom-right, soft outer halo. Tooltip
 * appears on hover. The modal handles the actual recording + parsing;
 * this is just the entry point.
 */
import React, { useState } from 'react';
import SitInListener, { MeetingResult } from './SitInListener';

// Matches Dashboard's exact gold (bronze-gold, not the lighter
// yellow-gold CLAUDE.md specifies). The FAB sits on top of the
// Dashboard's white background, so the colors here are picked to
// look identical to the top-bar buttons (MAP / BOARD / LIBRARY).
const ACCENT  = '#b8956a';
const ACCENT2 = '#9d7d56';

interface Props {
  token: string;
}

export default function SitInFab({ token }: Props) {
  const [open, setOpen]   = useState(false);
  const [hover, setHover] = useState(false);

  // Persist a captured meeting to the Board.
  //   • If Board is currently mounted, the `coast:meeting-saved` event
  //     gets picked up immediately — a card lands in the active board.
  //   • If Board isn't mounted (user was on Map / Dashboard / etc.),
  //     we stash the meeting in localStorage. Board's mount sweep
  //     picks it up the next time the user navigates there.
  // Belt-and-suspenders: we always do BOTH so a save is never lost.
  const handleSave = (data: MeetingResult) => {
    const pending = {
      recorded_at:  data.recorded_at,
      duration_sec: data.duration_sec,
      transcript:   data.transcript,
      parsed:       data.parsed,
    };
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    try {
      localStorage.setItem(
        `coast.meeting.pending.${id}`,
        JSON.stringify(pending),
      );
    } catch {
      // Quota or private mode — fall back to in-memory only.
    }
    // Cross-page "last saved" marker. RecentMeetingPill (mounted in
    // App.tsx) reads this and renders a click-to-jump pill on any
    // non-Board page so the user can navigate back to the meeting
    // they just made. Auto-dismisses after 10 minutes via the pill's
    // own staleness check.
    try {
      const title = data.parsed?.summary
                 || `Meeting · ${Math.round((data.duration_sec || 0) / 60)} min`;
      localStorage.setItem('coast.last_saved_meeting', JSON.stringify({
        id, title, recorded_at: data.recorded_at, saved_at: Date.now(),
      }));
      window.dispatchEvent(new Event('coast:last-meeting-changed'));
    } catch {}
    try {
      window.dispatchEvent(new CustomEvent('coast:meeting-saved',
        { detail: pending }));
      // If the event got picked up by a mounted Board, it cleared the
      // localStorage entry. We don't know that here, but the Board's
      // mount sweep is idempotent (it removes the key after pickup),
      // so duplicates are impossible.
    } catch {}

    const onBoard = window.location.pathname === '/board';
    const toast = document.createElement('div');
    toast.textContent = onBoard
      ? 'Meeting added to the Board.'
      : 'Meeting saved. It will appear on the Board next time you open it.';
    // Toast palette matches Dashboard so it reads as a continuation
    // of the app, not a separate modal layer.
    Object.assign(toast.style, {
      position: 'fixed', bottom: '92px', right: '24px',
      background: '#FAFAF8', color: '#0D3D5C',
      border: `1px solid ${ACCENT}`,
      padding: '10px 16px', borderRadius: '4px',
      fontFamily: '"EB Garamond", Georgia, serif', fontSize: '13px',
      zIndex: '9999', boxShadow: '0 6px 24px rgba(0,0,0,0.15)',
      letterSpacing: '0.02em',
    } as CSSStyleDeclaration);
    document.body.appendChild(toast);
    setTimeout(() => { try { document.body.removeChild(toast); } catch {} }, 3500);
  };

  return (
    <>
      {/* The button itself. Bottom-right, doesn't intercept clicks
          outside its circle, doesn't move when the page scrolls. */}
      <button
        onClick={() => setOpen(true)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-label="Sit in on a meeting"
        title="Sit in on a meeting · listen, transcribe, summarize"
        style={{
          position: 'fixed',
          bottom: 24, right: 24,
          width: 56, height: 56, borderRadius: '50%',
          background: ACCENT, color: '#0a1322',
          border: 'none', cursor: 'pointer',
          fontFamily: 'inherit',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: hover
            ? `0 0 0 8px rgba(201,169,110,0.18), 0 8px 24px rgba(0,0,0,0.45)`
            : `0 0 0 4px rgba(201,169,110,0.12), 0 4px 16px rgba(0,0,0,0.35)`,
          transition: 'box-shadow .2s, transform .2s',
          transform: hover ? 'scale(1.04)' : 'scale(1)',
          zIndex: 7000,
        }}
      >
        {/* Microphone glyph (inline SVG so we don't depend on any icon lib) */}
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
             stroke="#0a1322" strokeWidth="2"
             strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="2" width="6" height="13" rx="3" ry="3" fill={ACCENT2}/>
          <path d="M5 11v1a7 7 0 0 0 14 0v-1" />
          <line x1="12" y1="19" x2="12" y2="22" />
          <line x1="9" y1="22" x2="15" y2="22" />
        </svg>
      </button>

      {open && (
        <SitInListener
          token={token}
          onClose={() => setOpen(false)}
          onSave={handleSave}
        />
      )}
    </>
  );
}
